/**
 * Pure helpers for PaginationFilter.
 * Generic filter and sort payload builders. The parent owns API calls.
 * Do not import Transaction Matching utils — this component stays generic.
 */

/** Tab / apply source. Saved persists; playground applies for this view only. */
export const APPLY_SOURCE = {
  SAVED: 'saved',
  PLAYGROUND: 'playground',
};

/** Connector between groups, and between conditions inside one group. */
export const GROUP_CONNECTOR = {
  AND: 'AND',
  OR: 'OR',
};

/** Sort direction stored on each sort item. */
export const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
};

/** Operator used when a column change invalidates the previous choice. */
export const DEFAULT_OPERATOR = 'exact';

/** Apply is disabled while the parent reloads the list. The tooltip sits on a wrapper so the disabled button can still be hovered. */
export const APPLY_LOADING_TOOLTIP = 'Available when the list has finished loading';

/** Null and undefined. Empty string is a real filter value and is not nil. */
function isNil(value) {
  return value === null || value === undefined;
}

/**
 * Operators the backend filter helper accepts.
 * `types` limits which column types offer the operator.
 * Date columns show "Is" and send `contains`. The server matches that UTC calendar day
 * on a date field. String and number columns still send `exact`.
 * @type {Array<{ label: string, value: string, types: string[] }>}
 */
export const FILTER_OPERATORS = [
  { label: 'Is', value: 'contains', types: ['date'] },
  { label: 'Exact', value: 'exact', types: ['string', 'number'] },
  { label: 'Not Equals', value: 'not_equals', types: ['string', 'number'] },
  { label: 'Contains', value: 'contains', types: ['string'] },
  { label: 'Not Contains', value: 'not_contains', types: ['string'] },
  { label: 'Starts With', value: 'starts_with', types: ['string'] },
  { label: 'Ends With', value: 'ends_with', types: ['string'] },
  { label: 'Greater Than', value: 'greater_than', types: ['number', 'date'] },
  { label: 'Greater Than or Equal', value: 'greater_than_or_equal', types: ['number'] },
  { label: 'Less Than', value: 'less_than', types: ['number', 'date'] },
  { label: 'Less Than or Equal', value: 'less_than_or_equal', types: ['number'] },
  { label: 'Is Null', value: 'is_null', types: ['string', 'number', 'date'] },
  { label: 'Is Not Null', value: 'is_not_null', types: ['string', 'number', 'date'] },
];

/**
 * Operators offered for a column type. Unknown types use the string list.
 * @param {string} type - 'string' | 'number' | 'date'
 * @returns {Array<{ label: string, value: string, types: string[] }>}
 */
export function operatorsForType(type) {
  const fieldType = type === 'number' || type === 'date' ? type : 'string';
  return FILTER_OPERATORS.filter((operator) => operator.types.includes(fieldType));
}

/**
 * Operator used when the current one is not valid for the column type.
 * Date columns start on "Is" (`contains`). Other types start on Exact.
 * @param {string} type
 * @returns {string}
 */
function defaultOperatorFor(type) {
  const allowed = operatorsForType(type);
  return allowed.length ? allowed[0].value : DEFAULT_OPERATOR;
}

/**
 * Operator stored on the request. A date "Is" row, including an older saved `exact`, is `contains`.
 * @param {Object} condition
 * @param {Array} fields
 * @returns {string}
 */
function operatorForPayload(condition, fields) {
  const type = resolveFieldType(fields, condition && condition.column);
  if (type === 'date' && condition && condition.operator === 'exact') return 'contains';
  return condition ? condition.operator : '';
}

/**
 * Operator shown in the editor. Saved date `exact` and `contains` both display as "Is".
 * @param {Object} condition
 * @param {Array} fields
 * @returns {string}
 */
function editorOperator(condition, fields) {
  const type = resolveFieldType(fields, condition && condition.column);
  const operator = type === 'date' && condition && condition.operator === 'exact'
    ? 'contains'
    : (condition && condition.operator);
  const allowed = operatorsForType(type);
  if (operator && allowed.some((item) => item.value === operator)) return operator;
  return defaultOperatorFor(type);
}

/**
 * True when the operator needs a value. is_null and is_not_null do not.
 * @param {string} operator
 * @returns {boolean}
 */
export function operatorRequiresValue(operator) {
  return operator !== 'is_null' && operator !== 'is_not_null';
}

/**
 * Column type from the parent field list. Missing or unknown types are strings.
 * @param {Array<{ key: string, type?: string }>} fields
 * @param {string} column
 * @returns {'string'|'number'|'date'}
 */
export function resolveFieldType(fields, column) {
  const match = (fields || []).find((field) => field && field.key === column);
  if (match && (match.type === 'number' || match.type === 'date')) return match.type;
  return 'string';
}

/**
 * Id for a new group or condition. Not stable across reloads; persisted ids are kept.
 * @returns {string}
 */
export function generateUniqueId() {
  return Math.random().toString(36).slice(2, 11);
}

/**
 * One empty condition. `column` is the first filterable field key when the parent has fields.
 * @param {string} [firstFieldKey]
 * @returns {{ id: string, column: string, operator: string, value: string }}
 */
export function createDefaultCondition(firstFieldKey) {
  return {
    id: generateUniqueId(),
    column: firstFieldKey || '',
    operator: DEFAULT_OPERATOR,
    value: '',
  };
}

/**
 * One group with a single empty condition. Conditions inside the group start as AND.
 * @param {string} [firstFieldKey]
 * @returns {{ id: string, name: string, connector: string, conditions: Array }}
 */
export function createDefaultFilterGroup(firstFieldKey) {
  return {
    id: generateUniqueId(),
    name: '',
    connector: GROUP_CONNECTOR.AND,
    conditions: [createDefaultCondition(firstFieldKey)],
  };
}

/**
 * Payload shape for "no filters".
 * @returns {{ groups: Array, group_connectors: Array }}
 */
export function emptyFilters() {
  return { groups: [], group_connectors: [] };
}

/**
 * Playground/applied filter state from a list response.
 * Null, missing, or empty appliedFilters stays empty.
 * defaultFilters are a saved view and must not be passed here.
 * @param {Object|null} appliedFilters
 * @returns {{ groups: Array, group_connectors: Array }}
 */
export function playgroundFiltersFromApplied(appliedFilters) {
  const groups = appliedFilters && Array.isArray(appliedFilters.groups) ? appliedFilters.groups : [];
  if (!groups.length) return emptyFilters();
  return {
    groups,
    group_connectors: Array.isArray(appliedFilters.group_connectors)
      ? appliedFilters.group_connectors.slice()
      : [],
  };
}

/**
 * Copy filters and fill any missing group or condition ids.
 * Invalid input becomes emptyFilters(). Does not invent a blank group — the editor does that.
 * @param {Object} filters
 * @returns {{ groups: Array, group_connectors: Array }}
 */
export function ensureFilterIds(filters) {
  if (!filters || !Array.isArray(filters.groups)) return emptyFilters();

  return {
    group_connectors: Array.isArray(filters.group_connectors) ? filters.group_connectors.slice() : [],
    groups: filters.groups.filter(Boolean).map((group) => {
      const seen = new Set();
      const conditions = (Array.isArray(group.conditions) ? group.conditions : []).filter(Boolean).map((condition) => {
        let id = condition.id || generateUniqueId();
        while (seen.has(id)) id = generateUniqueId();
        seen.add(id);
        return {
          id,
          column: condition.column ? String(condition.column) : '',
          operator: condition.operator ? String(condition.operator) : DEFAULT_OPERATOR,
          value: isNil(condition.value) ? '' : condition.value,
        };
      });

      return {
        id: group.id || generateUniqueId(),
        name: group.name ? String(group.name) : '',
        connector: group.connector === GROUP_CONNECTOR.OR ? GROUP_CONNECTOR.OR : GROUP_CONNECTOR.AND,
        conditions,
      };
    }),
  };
}

/**
 * Connector between groups. A missing list means OR (match any group).
 * Only the first entry is edited in the UI; buildFilterPayload repeats it.
 * @param {Object} filters
 * @returns {'AND'|'OR'}
 */
export function groupingConnectorOf(filters) {
  const connectors = filters && Array.isArray(filters.group_connectors) ? filters.group_connectors : [];
  return connectors[0] === GROUP_CONNECTOR.AND ? GROUP_CONNECTOR.AND : GROUP_CONNECTOR.OR;
}

/**
 * Draft the builder can render: ids filled, and at least one group with one condition.
 * @param {Object} filters
 * @param {string} [firstFieldKey]
 * @returns {{ groups: Array, group_connectors: Array }}
 */
export function toEditorFilters(filters, firstFieldKey) {
  const ensured = ensureFilterIds(filters);
  const key = firstFieldKey || '';
  if (!ensured.groups.length) {
    return {
      groups: [createDefaultFilterGroup(key)],
      group_connectors: ensured.group_connectors.slice(),
    };
  }

  return {
    group_connectors: ensured.group_connectors.slice(),
    groups: ensured.groups.map((group) => ({
      ...group,
      conditions: group.conditions.length ? group.conditions.map((condition) => ({ ...condition })) : [createDefaultCondition(key)],
    })),
  };
}

/**
 * Apply one condition edit.
 * A column change resets the operator when the new type does not allow it.
 * A type change also clears the value when the operator string is still allowed.
 * Null operators clear the value so a hidden input cannot be submitted.
 * @param {Object} condition
 * @param {string} field - column | operator | value
 * @param {*} value
 * @param {Array} fields
 * @returns {Object}
 */
export function applyConditionPatch(condition, field, value, fields) {
  const previousType = field === 'column' ? resolveFieldType(fields, condition && condition.column) : '';
  const next = { ...condition, [field]: value };
  if (field === 'column') {
    const nextType = resolveFieldType(fields, value);
    const allowed = operatorsForType(nextType);
    if (!allowed.some((operator) => operator.value === next.operator)) {
      next.operator = defaultOperatorFor(nextType);
      next.value = '';
    } else if (previousType !== nextType) {
      // Date "Is" and string "Contains" share the contains value. Keep the operator, drop the old text.
      next.value = '';
    }
  }
  if (field === 'operator' && !operatorRequiresValue(value)) next.value = '';
  return next;
}

/**
 * Deep-clone JSON filter or sort state for open snapshots and emitted payloads.
 * @param {*} value
 * @returns {*}
 */
export function cloneState(value) {
  return JSON.parse(JSON.stringify(isNil(value) ? null : value));
}

/**
 * True when a condition has a column, a known operator for that column, and a value if required.
 * @param {Object} condition
 * @param {Array} fields
 * @returns {boolean}
 */
function isKeepableCondition(condition, fields) {
  if (!condition || !condition.column || !condition.operator) return false;
  const allowed = operatorsForType(resolveFieldType(fields, condition.column));
  if (!allowed.some((operator) => operator.value === operatorForPayload(condition, fields))) return false;
  if (!operatorRequiresValue(condition.operator)) return true;
  return coerceConditionValue(condition, fields) !== null;
}

/**
 * Trim strings. Numbers become numbers when the whole value parses. Dates keep yyyy-mm-dd.
 * @param {Object} condition
 * @param {Array} fields
 * @returns {string|number|null} null when the value should be dropped
 */
function coerceConditionValue(condition, fields) {
  const type = resolveFieldType(fields, condition.column);
  if (type === 'number') {
    if (typeof condition.value === 'number') return Number.isNaN(condition.value) ? null : condition.value;
    const trimmed = isNil(condition.value) ? '' : String(condition.value).trim();
    if (trimmed === '') return null;
    const numeric = Number(trimmed);
    return Number.isNaN(numeric) ? null : numeric;
  }

  const trimmed = isNil(condition.value) ? '' : String(condition.value).trim();
  if (trimmed === '') return null;
  if (type === 'date') {
    const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : trimmed;
  }
  return trimmed;
}

/**
 * API filter body. Drops blank rows. group_connectors length is groups.length - 1,
 * and every entry is the grouping connector from the editor toggle.
 * @param {Array} groups
 * @param {string} groupingConnector - 'AND' | 'OR'
 * @param {Array} [fields] - column definitions, used to coerce number and date values
 * @returns {{ groups: Array, group_connectors: string[] }}
 */
export function buildFilterPayload(groups, groupingConnector, fields) {
  const connector = groupingConnector === GROUP_CONNECTOR.AND ? GROUP_CONNECTOR.AND : GROUP_CONNECTOR.OR;
  const validGroups = (Array.isArray(groups) ? groups : [])
    .map((group) => {
      const conditions = (group && Array.isArray(group.conditions) ? group.conditions : [])
        .filter((condition) => isKeepableCondition(condition, fields))
        .map((condition) => {
          const next = {
            id: condition.id || generateUniqueId(),
            column: condition.column,
            operator: operatorForPayload(condition, fields),
          };
          if (operatorRequiresValue(condition.operator)) next.value = coerceConditionValue(condition, fields);
          return next;
        });
      if (!conditions.length) return null;
      return {
        id: group.id || generateUniqueId(),
        name: group.name ? String(group.name).trim() : '',
        connector: group.connector === GROUP_CONNECTOR.OR ? GROUP_CONNECTOR.OR : GROUP_CONNECTOR.AND,
        conditions,
      };
    })
    .filter(Boolean);

  return {
    groups: validGroups,
    group_connectors: validGroups.slice(1).map(() => connector),
  };
}

/**
 * Sort body for the API. Accepts { field, direction } or { key, direction }.
 * Drops rows without a field and keeps the first row when a field is repeated.
 * @param {Array} sort
 * @returns {Array<{ field: string, direction: 'asc'|'desc' }>}
 */
export function buildSortPayload(sort) {
  if (!Array.isArray(sort)) return [];
  const seen = new Set();
  const payload = [];
  sort.forEach((item) => {
    if (!item) return;
    const field = item.field || item.key;
    if (!field || seen.has(field)) return;
    seen.add(field);
    payload.push({
      field: String(field),
      direction: item.direction === SORT_DIRECTION.DESC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
    });
  });
  return payload;
}

/**
 * How many conditions buildFilterPayload would keep. Used for the Filters badge.
 * @param {Object} filters - { groups, group_connectors }
 * @param {Array} [fields]
 * @returns {number}
 */
export function countValidConditions(filters, fields) {
  const groups = filters && Array.isArray(filters.groups) ? filters.groups : [];
  const payload = buildFilterPayload(groups, groupingConnectorOf(filters), fields || []);
  return payload.groups.reduce((total, group) => total + group.conditions.length, 0);
}

/**
 * Filters button count. Pass the filters on the current query.
 * Applied filters replace the saved view, so this is one set, not a sum of both.
 * @param {Object} queryFilters
 * @param {Array} [fields]
 * @returns {number}
 */
export function filterBadgeCount(queryFilters, fields) {
  return countValidConditions(queryFilters, fields);
}

/**
 * Trial balance uses account-number ascending when the user has no sort.
 * That fallback is not a saved sort and not a playground sort.
 * @param {Array} sort
 * @returns {boolean}
 */
export function isSystemFallbackSort(sort) {
  const items = buildSortPayload(sort);
  return items.length === 1 && items[0].field === 'glcode' && items[0].direction === SORT_DIRECTION.ASC;
}

/**
 * @param {Array} left
 * @param {Array} right
 * @returns {boolean}
 */
export function sameSortPayload(left, right) {
  return JSON.stringify(buildSortPayload(left)) === JSON.stringify(buildSortPayload(right));
}

/**
 * Playground sort from response appliedSort.
 * Null, an exact match of the saved sort, and glcode ascending with nothing saved stay empty.
 * keepSystemFallback keeps that glcode sort when the user applied it themselves.
 * @param {Array|null} appliedSort
 * @param {Array} [savedSort]
 * @param {boolean} [keepSystemFallback]
 * @returns {Array<{ field: string, direction: 'asc'|'desc' }>}
 */
export function playgroundSortFromApplied(appliedSort, savedSort, keepSystemFallback) {
  const applied = buildSortPayload(appliedSort);
  if (!applied.length) return [];
  if (sameSortPayload(applied, savedSort)) return [];
  if (!keepSystemFallback && !buildSortPayload(savedSort).length && isSystemFallbackSort(applied)) return [];
  return applied;
}

/**
 * Sort button count. Pass the sort on the current query.
 * Applied sort replaces the saved sort. A lone glcode ascending sort stays at 0 when nothing is saved.
 * @param {Array} querySort
 * @param {Array} [savedSort]
 * @returns {number}
 */
export function sortBadgeCount(querySort, savedSort) {
  const query = buildSortPayload(querySort);
  if (!query.length) return 0;
  if (!buildSortPayload(savedSort).length && isSystemFallbackSort(query)) return 0;
  return query.length;
}

/**
 * Fill a blank column and replace an operator the column type does not allow.
 * Mutates the draft so existing row ids stay put.
 * @param {Object} filters
 * @param {Array} fields
 * @param {string} firstFieldKey
 * @returns {Object}
 */
export function prepareFilterDraft(filters, fields, firstFieldKey) {
  if (!filters || !Array.isArray(filters.groups)) return filters;
  filters.groups.forEach((group) => {
    (group.conditions || []).forEach((condition) => {
      if (!condition.column && firstFieldKey) condition.column = firstFieldKey;
      if (condition.operator) condition.operator = editorOperator(condition, fields);
    });
  });
  return filters;
}

/**
 * Copy of groups with one group's fields replaced.
 * @param {Array} groups
 * @param {string} groupId
 * @param {Object} patch
 * @returns {Array}
 */
export function patchGroup(groups, groupId, patch) {
  return (groups || []).map((group) => (group.id === groupId ? { ...group, ...patch } : group));
}

/**
 * Copy of groups with one condition edited via applyConditionPatch.
 * @param {Array} groups
 * @param {string} groupId
 * @param {string} conditionId
 * @param {string} field
 * @param {*} value
 * @param {Array} fields
 * @returns {Array}
 */
export function patchConditionInGroups(groups, groupId, conditionId, field, value, fields) {
  return (groups || []).map((group) => {
    if (group.id !== groupId) return group;
    return {
      ...group,
      conditions: (group.conditions || []).map((condition) => (condition.id === conditionId ? applyConditionPatch(condition, field, value, fields) : condition)),
    };
  });
}

/**
 * Copy of groups with a condition appended to one group.
 * @param {Array} groups
 * @param {string} groupId
 * @param {Object} condition
 * @returns {Array}
 */
export function addConditionInGroups(groups, groupId, condition) {
  return (groups || []).map((group) => (group.id === groupId ? { ...group, conditions: (group.conditions || []).concat([condition]) } : group));
}

/**
 * Copy of groups with a condition removed. The last condition in a group is replaced by fallback.
 * @param {Array} groups
 * @param {string} groupId
 * @param {string} conditionId
 * @param {Object} fallback
 * @returns {Array}
 */
export function removeConditionInGroups(groups, groupId, conditionId, fallback) {
  return (groups || []).map((group) => {
    if (group.id !== groupId) return group;
    const remaining = (group.conditions || []).filter((condition) => condition.id !== conditionId);
    return { ...group, conditions: remaining.length ? remaining : [fallback] };
  });
}

/**
 * Toggle a field in a sort list. New fields sort ascending and stay at the end.
 * @param {Array<{ field: string, direction: string }>} items
 * @param {string} field
 * @returns {Array}
 */
export function toggleSortField(items, field) {
  const current = (items || []).slice();
  const index = current.findIndex((item) => item.field === field);
  if (index >= 0) current.splice(index, 1);
  else current.push({ field, direction: SORT_DIRECTION.ASC });
  return current;
}

/**
 * Set asc or desc on one sort field. Unknown directions become asc.
 * @param {Array} items
 * @param {string} field
 * @param {string} direction
 * @returns {Array}
 */
export function withSortDirection(items, field, direction) {
  const nextDirection = direction === SORT_DIRECTION.DESC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC;
  return (items || []).map((item) => (item.field === field ? { ...item, direction: nextDirection } : item));
}

/**
 * 1-based page to open, or null when the text is blank, not an integer, out of range, or already current.
 * @param {*} raw
 * @param {number} page
 * @param {number} pageCount
 * @returns {number|null}
 */
export function parsePageJump(raw, page, pageCount) {
  const text = String(isNil(raw) ? '' : raw).trim();
  if (!/^\d+$/.test(text)) return null;
  const next = Number(text);
  if (next < 1 || next > pageCount || next === Number(page)) return null;
  return next;
}
